import { handleSendFeedbackEmail, useSupportStore } from '@/entities/support';
import { showMessageError, showMessageSuccess, checkNull } from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { t } from 'ttag';
import { facilitySelection } from '@/entities/facility';
import type { SendFeedbackModalProps } from './sendFeedbackModal.types';

/**
 * Holds the send-feedback modal's form: category, reply e-mail, message and facility. Reads the
 * support facilities from the support store, rebuilds the sorted facility options and preselects the
 * one remembered in local storage when they change, clears field errors as the e-mail or message is
 * edited, and wires the change, facility and send handlers, the last closing the modal when done.
 *
 * @param props - The toggleSendFeedbackModal callback that closes the modal.
 * @returns An object carrying the category list ref, the form state, and the change,
 * facility-change and send handlers.
 */
export const useSendFeedbackModal = ({
  toggleSendFeedbackModal,
}: Pick<SendFeedbackModalProps, 'toggleSendFeedbackModal'>) => {
  /**
   * Holds the list of support facilities kept in the support store. Read through a useSupportStore selector on the supportFacilities field.
   *
   * @returns The support facilities currently held in the store.
   */
  const supportFacilitiesRedux = useSupportStore((s) => s.supportFacilities);

  const categoryList = useRef([
    {
      label: t`Feedback`,
      value: 'Feedback',
    },
    {
      label: t`Feature request`,
      value: 'Feature request',
    },
  ]);

  const [state, setState] = useMergeState<any>({
    category: categoryList.current[0].value,
    replyEmail: '',
    content: '',
    errorObject: {},
    isSaving: false,
    formattedSupportFacilities: [],
    selectedFacility: null,
  });

  /**
   * Stores one field of the send-feedback modal form by its name. The name and value pair is merged into state as a dynamic key.
   *
   * @param name - State key of the edited field.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Stores the facility chosen in the send-feedback modal's select. The whole selected option object is merged into state under the field name so the label travels together with the value.
   *
   * @param name - State key holding the facility selection.
   * @param value - Raw value of the chosen option, unused.
   * @param option - Selected facility option stored in state.
   * @returns Nothing.
   */
  const onChangeFacility = (name: any, value: any, option: any) => {
    setState({ [name]: option });
  };

  /**
   * Validates and submits the message typed into the send-feedback modal. Required replyEmail and content are validated into errorObject first; otherwise isSaving is raised, the category, trimmed email, content and facility id are sent with a thank-you toast on success or an error toast on failure, and the modal is closed either way.
   *
   * @returns Promise resolved once the feedback is sent or the error reported.
   */
  const onClickSendMessage = async () => {
    const checkNullData = [
      { field: 'replyEmail', value: state.replyEmail, isRequired: true },
      { field: 'content', value: state.content, isRequired: true },
    ];
    const errorObject = checkNull(checkNullData);
    if (!_.isEmpty(errorObject)) {
      setState({ errorObject });
    } else {
      try {
        setState({ isSaving: true });
        await handleSendFeedbackEmail({
          category: state.category,
          replyEmail: state.replyEmail.trim(),
          content: state.content,
          facilityId: state.selectedFacility?.value,
        });
        showMessageSuccess({
          content: t`Your feedback has been sent. Thank you!`,
        });
        setState({
          replyEmail: '',
          content: '',
          isSaving: false,
        });
      } catch (error) {
        console.error('Failed to send feedback: ', error);
        showMessageError({ content: error instanceof Error ? error.message : undefined });
      } finally {
        toggleSendFeedbackModal?.();
      }
    }
  };

  useEffect(() => {
    /**
     * Support facilities mapped into select options carrying the facility name as label and its id as value. Ordered ascending by label with lodash orderBy.
     *
     * @returns Facility select options sorted by label.
     */
    const formattedSupportFacilities = _.orderBy(
      _.map(supportFacilitiesRedux, (supportFacility) => ({
        label: supportFacility.name,
        value: supportFacility.id,
      })),
      ['label'],
      ['asc'],
    );
    const selectedFacilityLocalStorage = facilitySelection.selectedFacilityLocalStorage();
    /**
     * The support facility that the send-feedback modal should preselect. Looked up among the formatted support facility options by the value remembered in local storage, falling back to the first available option.
     *
     * @returns Facility option to preselect.
     */
    const selectedFacility =
      _.find(formattedSupportFacilities, (x) => x.value === selectedFacilityLocalStorage?.value) ||
      formattedSupportFacilities[0];
    setState({ formattedSupportFacilities, selectedFacility });
  }, [supportFacilitiesRedux]);

  useUpdateEffect(() => {
    if (!_.isEmpty(state.errorObject)) {
      setState({ errorObject: {} });
    }
  }, [state.replyEmail, state.content]);

  return {
    categoryList,
    state,
    onChange,
    onChangeFacility,
    onClickSendMessage,
  };
};
